// src/pages/CarPage.js
import styled from "styled-components";

import Newsletter from "../components/Newsletter";
import Footer from "../components/Footer";
import { useLocation } from "react-router";
import { useEffect, useState } from "react";
import { publicRequest } from "../requestMethods";
import { useNavigate } from "react-router-dom";

import ArrowLeftOutlinedIcon from "@mui/icons-material/ArrowLeftOutlined";
import ArrowRightOutlinedIcon from "@mui/icons-material/ArrowRightOutlined";

/* ---------------- Slider Styles ---------------- */

const SliderContainer = styled.div`
  width: 100%;
  height: 500px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;

  @media only screen and (max-width: 768px) {
    height: 280px;
    border-radius: 8px;
  }
`;

const Arrow = styled.div`
  width: 50px;
  height: 50px;
  background-color: #fff7f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${(props) => props.direction === "left" && "10px"};
  right: ${(props) => props.direction === "right" && "10px"};
  margin: auto;
  cursor: pointer;
  opacity: 0.6;
  z-index: 2;

  @media only screen and (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const SliderWrapper = styled.div`
  height: 100%;
  display: flex;
  transition: all 1s ease;
  transform: translateX(-${(props) => props.slideIndex * 100}%);
`;

const Slide = styled.div`
  min-width: 100%;
  height: 100%;
  display: flex;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const CarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

/* ---------------- Page Layout ---------------- */

const Container = styled.div``;

const Wrapper = styled.div`
  padding: 50px;
  display: flex;
  gap: 40px;

  @media only screen and (max-width: 768px) {
    padding: 15px;
    flex-direction: column;
    gap: 20px;
  }
`;

const InfoContainer = styled.div`
  flex: 1;
  padding: 0px 50px;

  @media only screen and (max-width: 768px) {
    padding: 0;
  }
`;

const Title = styled.h1`
  font-weight: 300;
  margin-bottom: 20px;

  @media only screen and (max-width: 768px) {
    font-size: 22px;
    margin-bottom: 10px;
  }
`;

const Detail = styled.p`
  font-size: 20px;
  margin: 10px 0;
  color: #444;

  @media only screen and (max-width: 768px) {
    font-size: 15px;
    margin: 6px 0;
  }
`;

const Description = styled.p`
  margin: 20px 0;
  font-size: 18px;
  line-height: 1.5;
  color: #555;

  @media only screen and (max-width: 768px) {
    font-size: 14px;
    margin: 12px 0;
  }
`;

const Price = styled.h2`
  font-size: 40px;
  font-weight: 700;
  color: #0a7cff;
  margin-top: 20px;

  @media only screen and (max-width: 768px) {
    font-size: 26px;
    margin-top: 12px;
  }
`;

/* ---------------- Component ---------------- */

const CarPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const id = location.pathname.split("/")[2];
  const [product, setProduct] = useState({});
  const [owner, setOwner] = useState(null);

  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const getProduct = async () => {
      try {
        const res = await publicRequest.get("AffordableCars/find/" + id);
        setProduct(res.data);

        if (res.data.owner) {
          const ownerRes = await publicRequest.get(
            "auth/user/" + res.data.owner
          );
          setOwner(ownerRes.data);
        }
      } catch (err) {
        console.log("Error loading car:", err);
      }
    };

    getProduct();
  }, [id]);

  useEffect(() => {
    if (product?.marque && product?.model && product?.year && owner?.storeName) {
      document.title = `${product.marque} ${product.model} — ${owner.storeName}`;
    }
  }, [product, owner]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* Build image array: main image + secondary images */
  const images = [
    product.img && `/uploads/${product.img}`,
    product.img1 && `/uploads/${product.img1}`,
    product.img2 && `/uploads/${product.img2}`,
    product.img3 && `/uploads/${product.img3}`,
    product.img4 && `/uploads/${product.img4}`,
    product.img5 && `/uploads/${product.img5}`,
    product.img6 && `/uploads/${product.img6}`,
  ].filter(Boolean);

  const handleClick = (direction) => {
    if (direction === "left") {
      setSlideIndex((prev) => (prev - 1 + images.length) % images.length);
    } else {
      setSlideIndex((prev) => (prev + 1) % images.length);
    }
  };

  return (
    <Container>
      <Wrapper>
        {/* ----------- Image Slider ----------- */}
        <SliderContainer>
          <Arrow direction="left" onClick={() => handleClick("left")}>
            <ArrowLeftOutlinedIcon />
          </Arrow>

          <SliderWrapper slideIndex={slideIndex}>
            {images.map((src, index) => (
              <Slide key={index}>
                <ImageContainer>
                  <CarImage src={src} alt={`car-${index}`} />
                </ImageContainer>
              </Slide>
            ))}
          </SliderWrapper>

          <Arrow direction="right" onClick={() => handleClick("right")}>
            <ArrowRightOutlinedIcon />
          </Arrow>
        </SliderContainer>

        {/* ----------- Info Section ----------- */}
        <InfoContainer>
          <Title>
            {product.country} • {product.marque} {product.model}
          </Title>

          <Detail>Year: {product.year}</Detail>
          <Detail>Odometer: {product.odometer} km</Detail>
          <Detail>Location: {product.location}</Detail>

          <Description>{product.description}</Description>

          <Detail
            style={{
              color: "#0a7cff",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/store/${owner._id}?tab=cars`)}
          >
            Store: {owner?.storeName}
          </Detail>

          <Detail>Mobile: {owner?.mobile}</Detail>

          <Price>{product.price} SR</Price>
        </InfoContainer>
      </Wrapper>

      <Newsletter />
      <Footer />
    </Container>
  );
};

export default CarPage;
